import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createCheckoutSession, getUserDetails, iParleySubscriptions } from '../../util/api';
import { userTypeOf } from '../../util/userType';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const SUBSCRIPTION_REQUEST = 'app/StripePayoutPage/SUBSCRIPTION_REQUEST';
export const SUBSCRIPTION_SUCCESS = 'app/StripePayoutPage/SUBSCRIPTION_SUCCESS';
export const SUBSCRIPTION_ERROR = 'app/StripePayoutPage/SUBSCRIPTION_ERROR';

export const CANCEL_SUBSCRIPTION_REQUEST = 'app/StripePayoutPage/CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'app/StripePayoutPage/CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_ERROR = 'app/StripePayoutPage/CANCEL_SUBSCRIPTION_ERROR';

export const SAVE_SUBSCRIPTION_REQUEST = 'app/StripePayoutPage/SAVE_SUBSCRIPTION_REQUEST';
export const SAVE_SUBSCRIPTION_SUCCESS = 'app/StripePayoutPage/SAVE_SUBSCRIPTION_SUCCESS';
export const SAVE_SUBSCRIPTION_ERROR = 'app/StripePayoutPage/SAVE_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
  saveSubscriptionSuccess: false,
  saveSubscriptionError: false,
  subscriptionInProgress: false,
  subscriptionSuccess: false,
  subscriptionError: false,
  cancelSubscriptionInProgress: false,
  cancelSubscriptionSuccess: false,
  cancelSubscriptionError: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case SUBSCRIPTION_REQUEST:
      return { ...state, subscriptionInProgress: true };
    case SUBSCRIPTION_SUCCESS:
      const { url } = payload || {};
      if (url) {
        window.location.href = url;
      } else if (payload?.immediateUpdate) {
        // In-place plan change (e.g. free-trial upgrade) — no hosted checkout
        // to redirect to. Reload so the UI reflects the new plan.
        window.location.reload();
      }

      return { ...state, subscriptionInProgress: false, subscriptionSuccess: true };
    case SUBSCRIPTION_ERROR:
      return { ...state, subscriptionInProgress: false, subscriptionError: true };

    case SAVE_SUBSCRIPTION_REQUEST:
      return { ...state, saveSubscriptionInProgress: true };
    case SAVE_SUBSCRIPTION_SUCCESS:
      return { ...state, saveSubscriptionInProgress: false, saveSubscriptionSuccess: true };
    case SAVE_SUBSCRIPTION_ERROR:
      return { ...state, saveSubscriptionInProgress: false, saveSubscriptionError: true };

    case CANCEL_SUBSCRIPTION_REQUEST:
      return { ...state, cancelSubscriptionInProgress: true, cancelSubscriptionError: null };
    case CANCEL_SUBSCRIPTION_SUCCESS:
      return { ...state, cancelSubscriptionInProgress: false, cancelSubscriptionSuccess: true, cancelSubscriptionError: null };
    case CANCEL_SUBSCRIPTION_ERROR:
      return { ...state, cancelSubscriptionInProgress: false, cancelSubscriptionError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

export const subscriptionRequest = () => ({
  type: SUBSCRIPTION_REQUEST,
});
export const subscriptionSuccess = (response) => ({
  type: SUBSCRIPTION_SUCCESS,
  payload: response,
});
export const subscriptionError = () => ({
  type: SUBSCRIPTION_ERROR,
});

export const saveSubscriptionRequest = () => ({
  type: SAVE_SUBSCRIPTION_REQUEST,
});
export const saveSubscriptionSuccess = () => ({
  type: SAVE_SUBSCRIPTION_SUCCESS,
});
export const saveSubscriptionError = () => ({
  type: SAVE_SUBSCRIPTION_ERROR,
});

export const cancelSubscriptionRequest = () => ({
  type: CANCEL_SUBSCRIPTION_REQUEST,
});
export const cancelSubscriptionSuccess = () => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
});
export const cancelSubscriptionError = (e) => ({
  type: CANCEL_SUBSCRIPTION_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};

export const handleSubscriptionDuck = (priceId, quantity=1, type) => async (dispatch, getState, sdk) => {
  dispatch(subscriptionRequest());

  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListing = state.user?.profileListing;
  
  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    // let profileListing = await sdk.listings.show({ id: profileListingId });
    // profileListing = profileListing?.data?.data;

    email = currentUser?.attributes?.email;

    adminId = profileListing?.attributes?.publicData?.adminId;
    // if (adminId) {
    //   const adminUserResponse = await getUserDetails({ userId: adminId });
    //   const adminUser = adminUserResponse?.data?.data;
    //   email = adminUser?.attributes?.email;
    // }
  }

  if (!email) {
    return;
  }

  return createCheckoutSession({ priceId, quantity, type, email, paidBy: adminId, isSubscription: true })
    .then(response => {
      dispatch(subscriptionSuccess(response));
      return response;
    })
    .catch(() => {
      dispatch(subscriptionError())
    });
};

export const handleChangeSubscriptionDuck = (priceId, quantity=1, type, oldSubscriptionId) => async (dispatch, getState, sdk) => {
  dispatch(subscriptionRequest());

  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListing = state.user?.profileListing;
  
  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    // let profileListing = await sdk.listings.show({ id: profileListingId });
    // profileListing = profileListing?.data?.data;

    email = currentUser?.attributes?.email;

    adminId = profileListing?.attributes?.publicData?.adminId;
    // if (adminId) {
    //   const adminUserResponse = await getUserDetails({ userId: adminId });
    //   const adminUser = adminUserResponse?.data?.data;
    //   email = adminUser?.attributes?.email;
    // }
  }

  if (!email) {
    return;
  }

  return createCheckoutSession({ priceId, quantity, type, email, paidBy: adminId, subscriptionId: oldSubscriptionId, isChangeSubscription: true })
    .then(response => {
      dispatch(subscriptionSuccess(response));
      return response;
    })
    .catch(() => {
      dispatch(subscriptionError())
    });
};

export const handleCancelSubscriptionDuck = (subscription) => async (dispatch, getState, sdk) => {
  dispatch(cancelSubscriptionRequest());

  const toSendData = {
    subscription,
    cancelSubscription: true,
  }

  return iParleySubscriptions(toSendData)
    .then(response => {
      dispatch(cancelSubscriptionSuccess(response));
      return response;
    })
    .catch((e) => {
      dispatch(cancelSubscriptionError(e));
    });
};

// Undo a scheduled cancellation. Reuses the cancel-in-progress flag so the
// Resume button gets a loading spinner.
export const handleResumeSubscriptionDuck = (subscription) => async (dispatch) => {
  dispatch(cancelSubscriptionRequest());

  const toSendData = {
    subscription,
    resumeSubscription: true,
  };

  return iParleySubscriptions(toSendData)
    .then(response => {
      dispatch(cancelSubscriptionSuccess(response));
      return response;
    })
    .catch((e) => {
      dispatch(cancelSubscriptionError(e));
    });
};

export const saveSubscriptionDuck = (type, priceId, session_id) => async (dispatch, getState, sdk) => {
  dispatch(saveSubscriptionRequest());
  
  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListingAdminId = state.user?.profileListingAdminId;
  
  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    adminId = profileListingAdminId;
  }

  if (!email) {
    // return;
  }
  return;
  // return iParleySubscriptions({ type, email, userId: adminId, profileListingId: profileListingId, priceId: priceId, session_id: session_id, actionType: 'create' })
  //   .then(response => {
  //     dispatch(saveSubscriptionSuccess(response));
  //     return response;
  //   })
  //   .catch((error) => {
  //     dispatch(saveSubscriptionError(error));
  //   });

};
